import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast, Toast, ToastVariant } from '@hungryroot/components-toast';
import { Warning } from '@hungryroot/icons';
import { StatusCode } from '@hungryroot/rest';
import { deleteFavoriteProduct } from '@hungryroot/rest/api/v2/customers/[id]/favorite_products/[product_id]/delete';
import {
  getFavoriteProductsPath,
  type CustomerFavoriteProducts,
} from '@hungryroot/rest/api/v2/customers/[id]/favorite_products/get';
import { trackFavorite } from '../segment/track-favorite';

interface Params {
  customerId: number;
  productId: number;
  productName: string;
  segment: {
    location: 'card' | 'PDP';
  };
}

export function useDeleteFavoriteProduct() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { data: Params }) => {
      const { data } = params;
      const res = await deleteFavoriteProduct(data);
      if (res.status !== StatusCode.SuccessNoContent) {
        throw new Error(JSON.stringify(res.data, null, 2));
      }
      return res.data;
    },
    onMutate: async ({ data }) => {
      const { customerId, productId: deletedProductId } = data;

      await queryClient.cancelQueries({
        queryKey: [getFavoriteProductsPath({ customerId })],
      });

      const previousFavoriteProducts: CustomerFavoriteProducts | undefined =
        queryClient.getQueryData([getFavoriteProductsPath({ customerId })]);

      const nextFavoriteProducts =
        previousFavoriteProducts?.favorite_products.filter(
          (obj) => obj.product_id !== deletedProductId
        );
      queryClient.setQueryData([getFavoriteProductsPath({ customerId })], {
        favorite_products: nextFavoriteProducts,
      });

      return { previousFavoriteProducts, nextFavoriteProducts };
    },
    onError: (err, { data }, context) => {
      const { customerId } = data;

      toast(
        <Toast variant={ToastVariant.Critical}>
          <Warning />
          Unable to complete action. Please try again.
        </Toast>
      );

      queryClient.setQueryData(
        [getFavoriteProductsPath({ customerId })],
        context?.previousFavoriteProducts
      );
    },
    onSettled: (data, err, variables) => {
      const filterParam = 'is_favorite=1';

      queryClient.invalidateQueries({
        queryKey: [
          getFavoriteProductsPath({ customerId: variables.data.customerId }),
        ],
      });

      // Invalidate product filter queries that include favorites
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'ProductsV2' &&
          typeof query.queryKey[1] === 'string' &&
          query.queryKey[1].includes(filterParam),
      });
    },
    onSuccess: (data, variables) => {
      const { productId, productName, segment } = variables.data;

      trackFavorite({
        id: productId,
        name: productName,
        category: 'products',
        location: segment.location,
        state: false,
      });
    },
  });
}
